import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Invoice } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { usePayInvoiceApi } from '@hero/api-hooks';
import { fileToBase64 } from '@hero/web-utils';
import { toast } from 'sonner';
import { UploadIcon } from '@hero/web-icons';
import { EditDrawer } from '../../../app/Drawer/EditDrawer';
import { DateField } from '../../../app/Date/DateField';
import { PriceInput } from '../../../app/PriceInput/PriceInput';
import { FileUploader } from '../../../app/File/FileUploader/FileUploader';
import { Price } from '../../../app/Price';
import { Checkbox } from '../../../ui/Checkbox';
import { Label } from '../../../ui/Label';
import { Textarea } from '../../../ui/Textarea';

/**
 * Accept list for the optional transfer-receipt upload. Mirrors
 * the legacy `payment_flow.upload_hint` ("Supports JPG, PNG, or
 * PDF") — the lenders ingesting these for financing don't need a
 * VAT-style PDF; a screenshot is fine.
 */
const RECEIPT_FILE_TYPES: Record<string, string[]> = {
  'application/pdf': [],
  'image/jpeg': [],
  'image/png': [],
  'image/webp': [],
};

export interface PayInvoiceDrawerProps {
  /** Invoice to record a payment against. */
  invoice: Invoice | null;
  /** Drawer visibility — wire to local state. */
  open: boolean;
  /** Visibility setter. */
  onOpenChange: (open: boolean) => void;
  /**
   * Optional success callback fired AFTER the mutation resolves
   * and the toast has been queued. Useful when the host wants to
   * close a parent drawer / pop back to a list view.
   */
  onSuccess?: (paidInvoice: Invoice) => void;
}

/**
 * `PayInvoiceDrawer` — role-neutral "record an off-platform
 * payment" drawer.
 *
 * Used by both the seller (the receivable holder closing their
 * own invoice when the buyer paid by bank transfer) and the
 * buyer (recording that they sent the wire). Wraps the shared
 * `EditDrawer` chrome so the layout matches the rest of the
 * invoice-feature drawers (`UploadInvoiceProofDrawer`,
 * `EligibleLegsDrawer`).
 *
 * Fields:
 *   - Amount — pre-fills to `amount.remaining`; "Pay full"
 *     checkbox snaps it back to the remaining balance after the
 *     user has edited.
 *   - Pay date — defaults to today, allows back-dating (the
 *     `AccountTransaction.createdAt` and `Invoice.paidAt` both
 *     adopt this stamp, see `runInvoicePayment`).
 *   - Receipt — optional PDF / image; recommended for financing
 *     because lenders prefer transactions with an audit trail.
 *   - Notes — free-text passed straight to
 *     `AccountTransaction.notes`.
 */
export const PayInvoiceDrawer: React.FC<PayInvoiceDrawerProps> = ({
  invoice,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { t } = useTranslation(['m-payment', 'c-main']);

  const remaining = invoice?.amount.remaining ?? invoice?.amount.total ?? 0;
  const currency = invoice?.amount.currency ?? 'SAR';

  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [payFull, setPayFull] = useState(true);
  const [paidAt, setPaidAt] = useState<Date>(() => new Date());
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState('');

  // Re-seed every time the drawer opens against a (potentially
  // different) invoice. The fields are user-editable while open,
  // but a fresh open should always start from the canonical
  // defaults — full remaining, today, no file, no notes.
  useEffect(() => {
    if (!open) return;
    setAmount(remaining);
    setPayFull(true);
    setPaidAt(new Date());
    setFiles([]);
    setNotes('');
  }, [open, invoice?._id, remaining]);

  const { mutateAsync: payInvoice, isPending } = usePayInvoiceApi({
    onSuccess: data => {
      toast.success(
        t('m-payment:toast.success.payment_recorded', {
          defaultValue: 'Payment recorded',
        }),
      );
      onOpenChange(false);
      onSuccess?.(data.paidInvoice);
    },
    onError: (error: Error) => {
      toast.error(
        t('m-payment:toast.error.payment_failed', {
          message: error.message,
          defaultValue: `Payment failed: ${error.message}`,
        }),
      );
    },
  });

  const handlePayFullChange = useCallback(
    (checked: boolean) => {
      setPayFull(checked);
      if (checked) setAmount(remaining);
    },
    [remaining],
  );

  const handleAmountChange = useCallback(
    (next: number | undefined) => {
      setAmount(next);
      // Any manual edit that doesn't exactly match the remaining
      // amount turns off the "Pay full" checkbox so the UI tells
      // the truth.
      if (next !== remaining) setPayFull(false);
      else setPayFull(true);
    },
    [remaining],
  );

  const hasFile = files.length > 0;
  const fileType = hasFile ? files[0]?.type : undefined;
  const fileIsAccepted = !hasFile || !!fileType && fileType in RECEIPT_FILE_TYPES;

  const isValid = useMemo(() => {
    if (!invoice) return false;
    if (amount === undefined || amount <= 0) return false;
    if (amount > remaining + 0.01) return false;
    if (!paidAt) return false;
    if (hasFile && !fileIsAccepted) return false;
    return true;
  }, [invoice, amount, remaining, paidAt, hasFile, fileIsAccepted]);

  const handleSubmit = useCallback(async () => {
    if (!invoice || amount === undefined || !paidAt) return;
    try {
      const proofImage = hasFile
        ? { upload: await fileToBase64(files[0]) }
        : undefined;
      await payInvoice({
        invoiceId: getObjectId(invoice._id),
        amount,
        paidAt,
        proofImage,
        notes: notes.trim() || undefined,
      });
    } catch {
      // Surfaced by `onError` handler above.
    }
  }, [invoice, amount, paidAt, hasFile, files, notes, payInvoice]);

  const headerTitle = t('m-payment:record_payment.title', {
    defaultValue: 'Record payment',
  });

  return (
    <EditDrawer
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={headerTitle}
      submitLabel={t('m-payment:record_payment.submit', {
        defaultValue: 'Record payment',
      })}
      submitDisabled={!isValid}
      isSubmitting={isPending}
      onSubmit={handleSubmit}>
      <div className="flex flex-col gap-5">
        {invoice && (
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-wide text-emerald-700/80">
                  {t('m-payment:record_payment.remaining_label', {
                    defaultValue: 'Outstanding balance',
                  })}
                </span>
                <span className="text-base font-semibold text-emerald-900">
                  <Price amount={remaining} currency={currency} />
                </span>
              </div>
              {invoice.ref?.main && (
                <span className="rounded-md bg-white px-2 py-1 text-xs font-medium text-emerald-900 ring-1 ring-emerald-100">
                  #{invoice.ref.main}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Label htmlFor="record-payment-amount">
            {t('m-payment:record_payment.amount_label', {
              defaultValue: 'Amount',
            })}
            <span className="text-destructive ms-1">*</span>
          </Label>
          <PriceInput
            value={amount}
            onChange={handleAmountChange}
            currency={currency}
            capAmount={remaining}
            disabled={isPending || payFull}
          />
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={payFull}
              onCheckedChange={checked => handlePayFullChange(checked === true)}
              disabled={isPending}
            />
            {t('m-payment:record_payment.pay_full_label', {
              defaultValue: 'Pay full remaining balance',
            })}
          </label>
        </div>

        <div className="flex flex-col gap-1">
          <DateField
            id="record-payment-date"
            label={t('m-payment:record_payment.paid_at_label', {
              defaultValue: 'Pay date',
            })}
            required
            value={paidAt}
            onChange={day => day && setPaidAt(day)}
            maxDate={new Date()}
            placeholder={t('m-payment:record_payment.paid_at_placeholder', {
              defaultValue: 'Pick the date the payment cleared',
            })}
            disabled={isPending}
          />
          <p className="text-xs text-muted-foreground">
            {t('m-payment:record_payment.paid_at_hint', {
              defaultValue:
                'Defaults to today. Back-date to match when the funds actually cleared.',
            })}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="record-payment-receipt">
            {t('m-payment:record_payment.receipt_label', {
              defaultValue: 'Transfer receipt',
            })}
          </Label>
          <FileUploader
            files={hasFile ? files : undefined}
            onChange={setFiles}
            acceptedFileTypes={RECEIPT_FILE_TYPES}
            label={t('m-payment:record_payment.receipt_label', {
              defaultValue: 'Transfer receipt',
            })}
            variant="default"
            description={t('m-payment:record_payment.receipt_description', {
              defaultValue:
                'Optional. Attach the wire transfer screenshot or bank PDF — recommended for invoices you plan to finance.',
            })}
            hint={t('m-payment:record_payment.receipt_hint', {
              defaultValue: 'PDF, JPG, PNG',
            })}
            status={hasFile && !fileIsAccepted ? 'error' : hasFile ? 'success' : 'neutral'}
            statusMessage={
              hasFile && !fileIsAccepted
                ? t('m-payment:record_payment.receipt_type_error', {
                    defaultValue: 'Receipt must be a PDF or image.',
                  })
                : hasFile
                  ? t('m-payment:record_payment.receipt_selected', {
                      defaultValue: 'File selected',
                    })
                  : undefined
            }
            emptyIcon={<UploadIcon className="size-5 text-primary" />}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="record-payment-notes">
            {t('m-payment:record_payment.notes_label', {
              defaultValue: 'Notes',
            })}
          </Label>
          <Textarea
            id="record-payment-notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t('m-payment:record_payment.notes_placeholder', {
              defaultValue:
                'Add internal context — bank reference, payer name, …',
            })}
            disabled={isPending}
            rows={3}
          />
        </div>
      </div>
    </EditDrawer>
  );
};
